import {useForceRemountOnChange} from '@wandb/weave/common/util/hooks';
import React, {useEffect} from 'react';
import {Redirect} from 'react-router';

import NoMatch from '../../components/NoMatch';
import {InstrumentedLoader as Loader} from '../../components/utility/InstrumentedLoader';
import {useEntitySettings} from '../../util/entitySettings';
import history from '../../util/history';
import {trackPageViewed} from '../../util/navigation';
import {entity as entityPage} from '../../util/urls';
import EntityPageOld from '../EntityPageOld';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import ProfilePage from '../ProfilePage/ProfilePageNew';
import {TeamPageNew as TeamPage} from '../Team';
import {TeamPageContextProvider} from '../Team/TeamPageContext';
import {
  isFollowingInviteLinkAndToNewUser,
  useRedirectToSignupIfFollowingInviteLinkAndNotLoggedIn,
} from '../Team/utils';
import {LoaderProxy} from './../../containers/AppLoader';
import {EntityPageContextProvider} from './EntityPageContextProvider';
import {
  EntityPageData,
  EntityType,
  useEntityPageData,
} from './useEntityPageData';
import {useTrackProfileShareView} from './util';

type EntityPageProps = {
  match: {params: {entityName: string; filePath: string}};
  tab?: string;
};

type EntityPageWithDataProps = EntityPageProps & {
  entityPageData: EntityPageData;
};

const EntityPageWithData: React.FC<EntityPageWithDataProps> = React.memo(
  ({
    match: {
      params: {entityName, filePath},
    },
    tab,
    entityPageData: {
      data,
      error,
      initialLoading,
      isTeamMemberOrAdmin,
      isViewerLoggedIn,
    },
  }) => {
    useEffect(() => {
      trackPageViewed('Entity');
    }, []);

    const {isFollowingInviteLink, inviteIsToNewUser} =
      isFollowingInviteLinkAndToNewUser(entityName, 'Entity Page');

    useRedirectToSignupIfFollowingInviteLinkAndNotLoggedIn({
      isInviteToNewUser: isFollowingInviteLink && inviteIsToNewUser,
      isViewerLoggedIn,
    });

    /**
     * Control checks:
     * 1. Don't render a page until we know what kind of page it is
     * 2. Don't render for missing data
     * 3. If the entity has been claimed, redirect to the claiming entity instead so the original link still works.
     * 4. For a personal entity, render profile
     * 5. For a team entity, render team page
     * 6. Default to rendering missing data page for other cases, including organization entity type
     */
    // #1
    // TODO: figure out error case
    if (initialLoading || error) {
      return <Loader name="entity-page-loader" />;
    }

    // #2
    // data.available means "no entity exists with this name"
    if (data == null || data.available) {
      return <NoMatch />;
    }

    // #3
    if (data.claimingEntity != null) {
      return <Redirect to={entityPage(data.claimingEntity.name)} />;
    }

    // #4
    if (data.entityType === EntityType.Personal) {
      return <ProfilePage filePath={filePath} tab={tab} />;
    }

    // #5
    if (data.entityType === EntityType.Team) {
      return (
        <TeamPageContextProvider>
          <TeamPage entityName={entityName} tab={tab} />
        </TeamPageContextProvider>
      );
    }

    // #6
    return <NoMatch />;
  }
);
EntityPageWithData.displayName = 'EntityPageWithData';

const EntityPageProfilingWrapper: React.FC<EntityPageProps> = React.memo(
  props => {
    const {
      match: {
        params: {entityName},
      },
    } = props;
    useTrackProfileShareView(entityName);

    const {loading: entitySettingsLoading, entitySettings} =
      useEntitySettings(entityName);

    const {shouldRenderNull} = useForceRemountOnChange([entityName]);

    if (shouldRenderNull) {
      return null;
    }

    if (entitySettingsLoading) {
      return <LoaderProxy name="entity-page-profiling-wrapper-loader" />;
    }

    if (!entitySettings?.entityProfileEnabled) {
      return <EntityPageOld {...props} {...{history}} />;
    }

    return <EntityPageDataWrapper {...props} />;
  }
);
EntityPageProfilingWrapper.displayName = 'EntityPageProfilingWrapper';

export default EntityPageProfilingWrapper;

const EntityPageDataWrapper: React.FC<EntityPageProps> = props => {
  const {
    match: {
      params: {entityName},
    },
  } = props;
  const entityPageData = useEntityPageData(entityName);
  return (
    <EntityPageContextProvider {...{entityName, entityPageData}}>
      <EntityPageWithData {...props} {...{entityPageData}} />
    </EntityPageContextProvider>
  );
};
EntityPageDataWrapper.displayName = 'EntityPageDataWrapper';
